import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Package, ArrowRight } from "lucide-react";
import api from "@/api";
import { formatINR, formatDateRange } from "@/store/StoreContext";
import Seo from "@/components/store/Seo";
import { imgW, imgFallback } from "@/utils/img";

export default function OrderSuccess() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    const email = sessionStorage.getItem(`tq_order_email_${orderNumber}`) || "";
    api.get(`/orders/${orderNumber}`, { params: { email } }).then(({ data }) => {
      setOrder(data);
      if (data.payment?.status === "paid" || data.payment?.mode === "cod") {
        try {
          const fired = sessionStorage.getItem(`tq_purchase_${orderNumber}`);
          if (!fired) {
            sessionStorage.setItem(`tq_purchase_${orderNumber}`, "1");
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: "purchase", transaction_id: orderNumber, value: data.total, currency: "INR" });
            if (window.fbq) window.fbq("track", "Purchase", { value: data.total, currency: "INR" });
            if (window.gtag && window.__tqAds) {
              window.gtag("event", "conversion", { send_to: `${window.__tqAds.id}/${window.__tqAds.label}`, value: data.total, currency: "INR", transaction_id: orderNumber });
            }
          }
        } catch (e) { /* silent */ }
      }
    }).catch(() => setError(true));
  }, [orderNumber]);

  if (error) return (
    <div className="tq-container py-20 text-center">
      <p className="font-serif text-3xl mb-4">Order not found</p>
      <Link to="/" className="tq-btn-dark">Back to Home</Link>
    </div>
  );
  if (!order) return <div className="tq-container py-20"><div className="h-40 bg-[#FAFAFA] animate-pulse max-w-xl mx-auto" /></div>;

  return (
    <div className="tq-container py-12 sm:py-16 max-w-3xl" data-testid="order-success-page">
      <Seo title="Order Confirmed" noindex />
      <div className="text-center mb-10">
        <CheckCircle2 size={56} strokeWidth={1.2} className="mx-auto text-[#1A3C34] mb-4" />
        <h1 className="font-serif text-4xl sm:text-5xl">Thank You!</h1>
        <p className="text-[#666666] mt-3">Your order <strong className="text-[#111111]" data-testid="order-number">#{order.order_number}</strong> is {order.payment?.mode === "cod" ? "confirmed" : order.payment?.status === "paid" ? "confirmed & paid" : "placed (payment pending)"}.</p>
        {order.delivery_estimate && (
          <p className="mt-3 inline-flex items-center gap-2 bg-[#FAFAFA] px-4 py-2 text-sm" data-testid="success-estimate">
            <Package size={15} className="text-[#A8821B]" /> Estimated delivery: <strong>{formatDateRange(order.delivery_estimate)}</strong>
          </p>
        )}
      </div>
      <div className="tq-card p-6 mb-6">
        <p className="tq-overline mb-4">Order Summary</p>
        <ul className="divide-y divide-[#EAEAEA]">
          {order.items.map((it, i) => (
            <li key={i} className="py-4 flex gap-4">
              <img src={imgW(it.image, 200)} alt={it.name} loading="lazy" onError={imgFallback} className="w-16 h-20 object-cover bg-[#FAFAFA]" />
              <div className="flex-1">
                <p className="font-medium text-sm">{it.name}</p>
                <p className="text-xs text-[#C59B27]">Size {it.size} · Qty {it.qty}</p>
              </div>
              <p className="text-sm font-semibold">{formatINR(it.price * it.qty)}</p>
            </li>
          ))}
        </ul>
        <dl className="text-sm space-y-2 border-t border-[#EAEAEA] pt-4 mt-2">
          <div className="flex justify-between"><dt className="text-[#666666]">Subtotal</dt><dd>{formatINR(order.subtotal)}</dd></div>
          {order.discount > 0 && <div className="flex justify-between text-[#1A3C34]"><dt>Discount{order.coupon_code ? ` (${order.coupon_code})` : ""}</dt><dd>−{formatINR(order.discount)}</dd></div>}
          {order.prepaid_discount > 0 && <div className="flex justify-between text-[#1A3C34]"><dt>Prepaid Discount{order.prepaid_label ? ` (${order.prepaid_label})` : ""}</dt><dd data-testid="order-prepaid-discount">−{formatINR(order.prepaid_discount)}</dd></div>}
          <div className="flex justify-between"><dt className="text-[#666666]">Shipping{order.ship_label ? ` · ${order.ship_label}` : ""}</dt><dd>{order.shipping_discount > 0 ? <><span className="line-through text-[#8E8E93] mr-1.5">{formatINR(order.shipping_base)}</span><span className="text-[#1A3C34]">{order.shipping_fee ? formatINR(order.shipping_fee) : "FREE"}</span></> : order.shipping_fee ? formatINR(order.shipping_fee) : "FREE"}</dd></div>
          {order.cod_fee > 0 && <div className="flex justify-between"><dt className="text-[#666666]">COD Fee</dt><dd>{formatINR(order.cod_fee)}</dd></div>}
          <div className="flex justify-between font-semibold text-base border-t border-[#EAEAEA] pt-2"><dt>Total</dt><dd>{formatINR(order.total)}</dd></div>
          <div className="flex justify-between text-xs text-[#C59B27] pt-1"><dt>Payment</dt><dd className="uppercase">{order.payment?.mode === "cod" ? "Cash on Delivery" : `Online · ${order.payment?.status}`}</dd></div>
        </dl>
      </div>
      <div className="tq-card p-6 mb-8 text-sm">
        <p className="tq-overline mb-3">Shipping To</p>
        <p className="font-medium">{order.shipping_address?.first_name} {order.shipping_address?.last_name}</p>
        <p className="text-[#666666] mt-1">{order.shipping_address?.address}{order.shipping_address?.apartment ? `, ${order.shipping_address.apartment}` : ""}<br />{order.shipping_address?.city}, {order.shipping_address?.state} — {order.shipping_address?.pin}<br />{order.shipping_address?.phone}</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link to={`/pages/track-order?order=${order.order_number}`} data-testid="success-track-btn" className="tq-btn-dark">Track Order</Link>
        <Link to="/collections/new-arrivals" data-testid="success-continue-btn" className="tq-btn-outline">Continue Shopping <ArrowRight size={14} /></Link>
      </div>
    </div>
  );
}
